import React, { FunctionComponent } from "react";
import PropTypes from "prop-types";
import awaitingType from "./types/awaitingType";
import AwaitingActions from "./AwaitingActions";
import RejectForm from "./RejectForm";
import {
  Wrapper,
  Header,
  AgentWrapper,
  AgentDetails,
  Avatar,
  AvatarImage,
  TextDetails,
  Name,
  Company,
  Requested,
  Message,
  NameLink,
  Review,
  ReviewLink,
  StarRating,
  StarRatingLabel,
  FillRating,
  EmptyRating,
  Stars,
  NoRatings,
  HHScore,
  Score,
} from "./styles/awaiting-styles";

import { NewBadge, NewBadgeWrapper } from "./styles/new-badge-styles";

export interface IAgent {
  id: number;
  engagementId: number;
  jobId: number;
  name: string;
  company: string;
  respondedAt: string;
  message: string;
  reviewAgentUrl: string;
  acceptUrl: string;
  rejectUrl: string;
  avatar: string;
  starRatingPercent: number;
  isNew: boolean;
  hhScore: number;
}

export interface IAwaitingProps {
  agents: Array<IAgent>;
  acceptOnClick: (acceptUrl: string) => Promise<unknown>;
  rejectOnClick: (
    rejectUrl: string,
    engagementId: number,
    jobId: number
  ) => void;
  rejectModalShown: boolean;
  errors: unknown;
  formLoading: boolean;
  closeRejectModal: (e: React.MouseEvent<HTMLDivElement>) => void;
  handleRejectOnSubmit: (e: React.FormEvent<HTMLFormElement>) => Promise<void>;
  setRejectMessage: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
}

const Awaiting: FunctionComponent<IAwaitingProps> = (props) => {
  const {
    agents,
    acceptOnClick,
    rejectOnClick,
    rejectModalShown,
    errors,
    formLoading,
    closeRejectModal,
    handleRejectOnSubmit,
    setRejectMessage,
  } = props;

  return (
    <Wrapper>
      {rejectModalShown && (
        <RejectForm
          closeRejectModal={closeRejectModal}
          handleRejectOnSubmit={handleRejectOnSubmit}
          setRejectMessage={setRejectMessage}
          formLoading={formLoading}
          errors={errors}
        />
      )}
      <Header>New Engagements Awaiting Approval</Header>
      {agents.map((agent) => (
        <AgentWrapper key={agent.engagementId}>
          <AgentDetails>
            <Avatar>
              <AvatarImage src={agent.avatar} alt="Avatar" />
            </Avatar>
            <TextDetails>
              <Name>
                <NameLink
                  href={agent.reviewAgentUrl}
                  title={agent.name}
                  data-turbolinks="false"
                >
                  {agent.name}
                </NameLink>
              </Name>
              <Company>{agent.company}</Company>
              <Requested>Requested: {agent.respondedAt}</Requested>
              <HHScore>
                HH Score:{" "}
                {agent.isNew ? (
                  "no score yet"
                ) : (
                  <Score data-test-id="hh-score">{agent.hhScore}/10</Score>
                )}
              </HHScore>
              {agent.isNew && (
                <NewBadgeWrapper>
                  <NewBadge data-test-id="hh-new-badge">NEW!</NewBadge>
                </NewBadgeWrapper>
              )}
              {agent.starRatingPercent ? (
                <StarRating className="star-ratings">
                  <StarRatingLabel>Reviews:</StarRatingLabel>
                  <FillRating
                    className="fill-ratings"
                    style={{ width: `${agent.starRatingPercent}%` }}
                  >
                    <Stars>★★★★★</Stars>
                  </FillRating>
                  <EmptyRating className="empty-ratings">
                    <Stars>★★★★★</Stars>
                  </EmptyRating>
                </StarRating>
              ) : (
                <NoRatings>no ratings yet</NoRatings>
              )}
            </TextDetails>
          </AgentDetails>
          <Message>
            {'"'}
            {agent.message}
            {'"'}
          </Message>
          <Review>
            <ReviewLink
              href={agent.reviewAgentUrl}
              title="Review link"
              data-test-id="review_link"
            >
              Review Agent
            </ReviewLink>
          </Review>
          <AwaitingActions
            acceptUrl={agent.acceptUrl}
            rejectUrl={agent.rejectUrl}
            acceptOnClick={acceptOnClick}
            rejectOnClick={rejectOnClick}
            jobId={agent.jobId}
            engagementId={agent.engagementId}
          />
        </AgentWrapper>
      ))}
    </Wrapper>
  );
};

Awaiting.propTypes = {
  agents: awaitingType.isRequired,
  acceptOnClick: PropTypes.func.isRequired,
  rejectOnClick: PropTypes.func.isRequired,
  rejectModalShown: PropTypes.bool.isRequired,
  errors: PropTypes.shape({}).isRequired,
  formLoading: PropTypes.bool.isRequired,
  closeRejectModal: PropTypes.func.isRequired,
  handleRejectOnSubmit: PropTypes.func.isRequired,
  setRejectMessage: PropTypes.func.isRequired,
};

export default Awaiting;
